/**
 * Utility functions for working with dates and day names in the application
 */

/**
 * Get the day of the week in lowercase format from a date
 * @param date - The date to extract the day from
 * @returns The day of the week in lowercase (e.g., "monday")
 */
export const getDayOfWeekLowercase = (date: Date): string => {
  return date.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
};

/**
 * Get the day of the week with first letter capitalized from a date
 * @param date - The date to extract the day from
 * @returns The day of the week with first letter capitalized (e.g., "Monday")
 */
export const getDayOfWeekCapitalized = (date: Date): string => {
  const dayLower = getDayOfWeekLowercase(date);
  return dayLower.charAt(0).toUpperCase() + dayLower.slice(1);
};

/**
 * Check if a day name exists in an array, handling case-insensitive matching
 * @param dayName - The day to check for
 * @param daysArray - Array of day names to search in
 * @returns True if the day exists in the array (case-insensitive), false otherwise
 */
export const isDayInArray = (dayName: string, daysArray?: string[]): boolean => {
  if (!daysArray || !Array.isArray(daysArray) || daysArray.length === 0) {
    return false;
  }
  
  const dayLower = dayName.toLowerCase();
  const dayCapitalized = dayLower.charAt(0).toUpperCase() + dayLower.slice(1);
  
  return daysArray.some(day => 
    day.toLowerCase() === dayLower || 
    day === dayCapitalized
  );
};

/**
 * Get current time formatted as HH:MM for comparison with cutoff times
 * @returns Current time in HH:MM format
 */
export const getCurrentTimeFormatted = (): string => {
  const now = new Date();
  return now.getHours().toString().padStart(2, '0') + 
         ':' + 
         now.getMinutes().toString().padStart(2, '0');
};

/**
 * Find the cutoff time for a specific day, handling case-insensitive day names
 * @param dayName - The day to get the cutoff time for
 * @param cutoffTimes - The cutoff times object
 * @returns The cutoff time or undefined if not found
 */
export const findCutoffTime = (
  dayName: string, 
  cutoffTimes?: Record<string, string>
): string | undefined => {
  if (!cutoffTimes) {
    return undefined;
  }
  
  const dayLower = dayName.toLowerCase();
  const dayCapitalized = dayLower.charAt(0).toUpperCase() + dayLower.slice(1);
  
  return cutoffTimes[dayLower] || cutoffTimes[dayCapitalized];
};

/**
 * Check if a date is today
 * @param date - The date to check
 * @returns True if the date is today, false otherwise
 */
export const isDateToday = (date: Date): boolean => {
  const today = new Date();
  return date.toDateString() === today.toDateString();
}; 